package br.com.vvrefeicoes.portal.feature.operacional

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import br.com.vvrefeicoes.portal.core.di.LoadErrorState
import br.com.vvrefeicoes.portal.core.di.LocalAppGraph
import br.com.vvrefeicoes.portal.core.di.loadGuarded
import br.com.vvrefeicoes.portal.core.util.DateTimeProvider
import br.com.vvrefeicoes.portal.core.util.PeriodSelection
import br.com.vvrefeicoes.portal.core.util.SystemDateTimeProvider
import br.com.vvrefeicoes.portal.domain.model.CompanyRef
import br.com.vvrefeicoes.portal.domain.model.MonthlyFinancials
import br.com.vvrefeicoes.portal.domain.model.OrgChartData
import br.com.vvrefeicoes.portal.domain.model.UnitMargin
import br.com.vvrefeicoes.portal.domain.model.unidadeKeyOf
import br.com.vvrefeicoes.portal.domain.repository.OrgChartRepository
import br.com.vvrefeicoes.portal.domain.repository.OverviewRepository
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch

data class OverviewMetrics(
    val revenueTotalCents: Long = 0,
    val revenuePlannedCents: Long = 0,
    val expenseTotalCents: Long = 0,
    val expensePlannedCents: Long = 0,
    val resultCents: Long = 0,
    val fopagCents: Long = 0,
    val headcount: Int = 0,
    val transportedCents: Long = 0,
    val fopagPercent: Double = 0.0,
    val revenuePerFopag: Double = 0.0,
)

class OverviewController(
    private val repository: OverviewRepository,
    private val orgChartRepository: OrgChartRepository,
    private val scope: CoroutineScope,
    private val dateTimeProvider: DateTimeProvider = SystemDateTimeProvider(),
    private val loadError: LoadErrorState = LoadErrorState(),
) {
    var loading by mutableStateOf(true)
        private set

    var units by mutableStateOf<List<CompanyRef>>(emptyList())
        private set

    var selection by mutableStateOf(PeriodSelection.current(dateTimeProvider.currentPeriod()))
        private set

    var metrics by mutableStateOf(OverviewMetrics())
        private set

    var monthlyFinancials by mutableStateOf<List<MonthlyFinancials>>(emptyList())
        private set

    var unitMargins by mutableStateOf<List<UnitMargin>>(emptyList())
        private set

    var negativeUnitMargins by mutableStateOf<List<UnitMargin>>(emptyList())
        private set

    private var started = false
    private var unitFilter: CompanyRef? = null
    private var managerFilter: String? = null
    private var directorFilter: String? = null
    private var authorizedUnits: List<CompanyRef> = emptyList()
    private var orgChartData: OrgChartData? = null

    fun init() {
        if (started) return
        started = true
        scope.launch {
            loadError.loadGuarded { load() }
            loading = false
        }
    }

    fun applyPeriod(newSelection: PeriodSelection) {
        if (newSelection == selection) return
        selection = newSelection
        scope.launch { loadError.loadGuarded { load() } }
    }

    fun applyFilter(
        unit: CompanyRef?,
        manager: String?,
        director: String?,
        authorizedUnits: List<CompanyRef>,
    ) {
        if (unit == unitFilter && manager == managerFilter && director == directorFilter && authorizedUnits == this.authorizedUnits) return
        unitFilter = unit
        managerFilter = manager
        directorFilter = director
        this.authorizedUnits = authorizedUnits
        scope.launch { loadError.loadGuarded { load() } }
    }

    private suspend fun load() {
        val data =
            orgChartData ?: orgChartRepository
                .loadInitial()
                .currentVersion.data
                .also { orgChartData = it }
        units = authorizedUnits
        val unitId = unitFilter?.let { data.unidadeKeyOf(it) }
        val overview = repository.load(selection, unitId, managerFilter, directorFilter)
        metrics =
            OverviewMetrics(
                revenueTotalCents = overview.revenueRealCents,
                revenuePlannedCents = overview.revenuePlannedCents,
                expenseTotalCents = overview.expenseRealCents,
                expensePlannedCents = overview.expensePlannedCents,
                resultCents = overview.resultCents,
                fopagCents = overview.fopagCents,
                headcount = overview.headcount,
                transportedCents = overview.transportedCents,
                fopagPercent = overview.fopagPercent,
                revenuePerFopag = overview.revenuePerFopag,
            )
        monthlyFinancials = overview.evolution
        unitMargins = overview.unitMargins
        negativeUnitMargins = overview.negativeUnitMargins
    }
}

@Composable
fun rememberOverviewController(): OverviewController {
    val appGraph = LocalAppGraph.current
    val scope = rememberCoroutineScope()
    return remember {
        OverviewController(
            repository = appGraph.overviewRepository,
            orgChartRepository = appGraph.orgChartRepository,
            scope = scope,
            dateTimeProvider = appGraph.dateTimeProvider,
            loadError = appGraph.loadError,
        )
    }
}
